import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getSupabaseClient } from "../providers/supabase/supabase";

export type RuntimeVarClass = "secret" | "flag" | "plain";
export type RuntimeVarClassFilter = RuntimeVarClass | "all";
export type RuntimeEnvironmentKey = "stg" | "staging";

export type RuntimeVar = {
  name: string;
  class: RuntimeVarClass;
  /** Never present for class === "secret" — and stripped client-side even if a
   * misbehaving server sends one. */
  value?: string;
  enabled?: boolean;
  /** Where the secret lives: secretsmanager | ssm | plaintext-env. */
  store?: string;
  container?: string;
};

export type RuntimeServiceStatus = "ok" | "deploying" | "absent" | "error";

export type RuntimeService = {
  id: string;
  label: string;
  kind: string;
  resource: string;
  revision?: string | null;
  latest_revision?: string | null;
  revision_pinned_by_state_machine?: boolean;
  revision_drift?: boolean;
  status: RuntimeServiceStatus;
  error?: string;
  vars: RuntimeVar[];
};

export type RuntimeFlagsPayload = {
  environment: string;
  environment_label: string;
  generated_at: string;
  cache: { ttl_seconds: number; age_seconds: number };
  services: RuntimeService[];
};

export type RuntimeFlagsState =
  | { status: "ok"; payload: RuntimeFlagsPayload }
  | { status: "unconfigured" }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

/**
 * Defensive client-side rule: a secret row must never carry a value, even if
 * a future server bug sends one. Strip at the trust boundary so no component
 * can accidentally render it.
 */
export function sanitizePayload(
  payload: RuntimeFlagsPayload,
): RuntimeFlagsPayload {
  return {
    ...payload,
    services: (payload.services ?? []).map((service) => ({
      ...service,
      vars: (service.vars ?? []).map((entry) => {
        if (entry.class !== "secret") return entry;
        const { value: _value, enabled: _enabled, ...rest } = entry;
        return rest;
      }),
    })),
  };
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function fetchRuntimeFlags(
  environment: RuntimeEnvironmentKey,
  refresh = false,
): Promise<RuntimeFlagsState> {
  const query = new URLSearchParams({ environment });
  if (refresh) query.set("refresh", "1");
  const url = `/api/runtime-flags?${query.toString()}`;
  let response: Response;
  try {
    response = await fetch(url, { headers: await authHeaders() });
  } catch (error) {
    return {
      status: "error",
      message: error instanceof Error ? error.message : "network error",
    };
  }
  if (response.status === 503) return { status: "unconfigured" };
  if (response.status === 401 || response.status === 403)
    return { status: "unauthorized" };
  if (!response.ok)
    return {
      status: "error",
      message: `runtime-flags responded ${response.status}`,
    };
  const payload = (await response.json()) as RuntimeFlagsPayload;
  return { status: "ok", payload: sanitizePayload(payload) };
}

export function useRuntimeFlags(environment: RuntimeEnvironmentKey) {
  const queryClient = useQueryClient();
  const queryKey = ["runtime-flags", environment] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => fetchRuntimeFlags(environment, false),
    staleTime: 30_000,
    retry: false,
  });
  const refreshMutation = useMutation({
    mutationFn: () => fetchRuntimeFlags(environment, true),
    onSuccess: (state) => queryClient.setQueryData(queryKey, state),
  });
  return {
    state: query.data ?? null,
    isLoading: query.isLoading,
    isRefreshing:
      refreshMutation.isPending || (query.isFetching && !query.isLoading),
    refresh: () => refreshMutation.mutateAsync(),
  };
}

export type FlagOverviewRow = {
  name: string;
  /** serviceId -> enabled (undefined when the service does not set the flag). */
  perService: Record<string, boolean | undefined>;
  /** True when services that DO set the flag disagree on its state. */
  mismatch: boolean;
};

/**
 * Pivot every class === "flag" var across services into one row per flag name,
 * flagging cross-service disagreements (the drift this tab exists to expose).
 */
export function buildFlagOverview(
  services: RuntimeService[],
): FlagOverviewRow[] {
  const rows = new Map<string, FlagOverviewRow>();
  for (const service of services) {
    for (const entry of service.vars) {
      if (entry.class !== "flag" || typeof entry.enabled !== "boolean")
        continue;
      let row = rows.get(entry.name);
      if (!row) {
        row = { name: entry.name, perService: {}, mismatch: false };
        rows.set(entry.name, row);
      }
      row.perService[service.id] = entry.enabled;
    }
  }
  for (const row of rows.values()) {
    const states = Object.values(row.perService).filter(
      (state): state is boolean => typeof state === "boolean",
    );
    row.mismatch = states.length > 1 && new Set(states).size > 1;
  }
  return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function matchesSearch(entry: RuntimeVar, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  if (entry.name.toLowerCase().includes(needle)) return true;
  return (
    entry.class !== "secret" &&
    (entry.value ?? "").toLowerCase().includes(needle)
  );
}
